// Client-side inactivity (idle) auto-logout. "Activity" = real user interaction
// only (mouse / keyboard / touch / scroll) — background polling never resets the
// timer. The last-activity timestamp lives in localStorage so it survives a browser
// close: returning after >4h = logged out, returning sooner = still signed in.
// Shared across tabs via the storage key.

export const IDLE_LIMIT_MS = 4 * 60 * 60 * 1000; // 4 hours
const ACTIVITY_KEY = "itx_last_activity";
const LOGOUT_KEY = "itx_idle_logout"; // cross-tab logout broadcast
const WRITE_THROTTLE_MS = 30 * 1000; // coalesce high-frequency events
const ACTIVITY_EVENTS = ["mousedown", "keydown", "touchstart", "scroll", "wheel", "pointerdown"];

let lastWrite = 0;

export function recordActivity(force = false) {
  const now = Date.now();
  if (!force && now - lastWrite < WRITE_THROTTLE_MS) return;
  lastWrite = now;
  try { localStorage.setItem(ACTIVITY_KEY, String(now)); } catch { /* ignore */ }
}

export function getLastActivity() {
  try {
    const v = parseInt(localStorage.getItem(ACTIVITY_KEY) || "", 10);
    return Number.isFinite(v) ? v : null;
  } catch { return null; }
}

export function clearActivity() {
  try { localStorage.removeItem(ACTIVITY_KEY); } catch { /* ignore */ }
}

export function isIdleExpired() {
  const last = getLastActivity();
  if (last == null) return false; // no marker yet -> not expired
  return Date.now() - last > IDLE_LIMIT_MS;
}

// Broadcast an idle-logout to other tabs (storage event fires in OTHER tabs only).
export function broadcastIdleLogout() {
  try { localStorage.setItem(LOGOUT_KEY, String(Date.now())); } catch { /* ignore */ }
}

// Wire up listeners + a periodic check. Returns a cleanup function.
// onActivity() is called (throttled) on real interaction; onExpire() when idle.
export function installIdleWatcher({ onActivity, onExpire, isLoggedIn }) {
  const handleActivity = () => { if (isLoggedIn()) { recordActivity(); onActivity && onActivity(); } };
  ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, handleActivity, { passive: true }));

  const check = () => { if (isLoggedIn() && isIdleExpired()) onExpire(); };
  const intervalId = setInterval(check, 60 * 1000);

  const onVisibility = () => { if (document.visibilityState === "visible") check(); };
  document.addEventListener("visibilitychange", onVisibility);

  const onStorage = (e) => {
    if (e.key === LOGOUT_KEY && isLoggedIn()) onExpire();   // another tab logged out
    if (e.key === ACTIVITY_KEY) check();                     // activity elsewhere; re-evaluate
  };
  window.addEventListener("storage", onStorage);

  return () => {
    ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, handleActivity));
    clearInterval(intervalId);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("storage", onStorage);
  };
}
